import { useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import { useAuth } from 'auth/AuthContext'
import ModalComponent from 'components/common/ModalComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import LabeledInputComponent from 'components/common/LabeledInputComponent'

const GenerateReportModal = ({ isOpen, onClose, onGenerate }) => {
  const { user } = useAuth()
  const [state, setState] = useState({
    reportType: 'leam-report',
    startDateTime: '',
    endDateTime: '',
    generating: false
  })

  const handleGenerate = async () => {
    // Validate dates
    if (!state.startDateTime || !state.endDateTime) {
      showAlert({ message: 'Please select both start and end dates', icon: 'error' })
      return
    }

    if (new Date(state.startDateTime) > new Date(state.endDateTime)) {
      showAlert({ message: 'Start date must be before end date', icon: 'error' })
      return
    }

    setState(s => ({ ...s, generating: true }))

    try {
      // Convert datetime-local format to ISO format with seconds
      const startISO = state.startDateTime.includes(':') 
        ? `${state.startDateTime}:00` 
        : state.startDateTime
      const endISO = state.endDateTime.includes(':') 
        ? `${state.endDateTime}:00` 
        : state.endDateTime

      const response = await makeRequest({
        method: 'GET',
        url: `/reports/${user.farmId}/leam?startDateTime=${encodeURIComponent(startISO)}&endDateTime=${encodeURIComponent(endISO)}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      })

      if (response.data?.success) {
        showAlert({ 
          message: response.data.message || 'Report generated successfully', 
          icon: 'success' 
        })
        
        // Reset form and close modal
        setState({
          reportType: 'leam-report',
          startDateTime: '',
          endDateTime: '',
          generating: false
        })
        onClose()
        
        // Refresh the reports list
        if (onGenerate) {
          onGenerate()
        }
      } else {
        showAlert({ 
          message: response.data?.message || 'Failed to generate report', 
          icon: 'error' 
        })
        setState(s => ({ ...s, generating: false }))
      }
    } catch (err) {
      showAlert({ 
        message: err.response?.data?.message || 'Failed to generate report', 
        icon: 'error' 
      })
      setState(s => ({ ...s, generating: false }))
    }
  }

  const handleClose = () => {
    if (!state.generating) {
      setState({
        reportType: 'leam-report',
        startDateTime: '',
        endDateTime: '',
        generating: false
      })
      onClose()
    }
  }

  return (
    <ModalComponent isOpen={isOpen} onClose={handleClose} title="Generate Report" hideDefaultFooter>
      <div className='bg-white rounded-lg shadow-xl max-w-md w-full'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>Generate Report</h3>
          <button
            onClick={handleClose}
            className='text-gray-400 hover:text-gray-600 transition-colors'
            disabled={state.generating}
          >
            <XMarkIcon className='w-6 h-6' />
          </button>
        </div>

        {/* Body */}
        <div className='p-6 space-y-4'>
          {/* Report Type */}
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Report Type
            </label>
            <select
              value={state.reportType}
              onChange={(e) => setState(s => ({ ...s, reportType: e.target.value }))}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              disabled={state.generating}
            >
              <option value='leam-report'>LEAM Report</option>
            </select>
          </div>

          {/* Start Date */}
          <LabeledInputComponent
            label='Start Date & Time'
            name='startDateTime'
            type='datetime-local'
            value={state.startDateTime}
            onChange={(e) => setState(s => ({ ...s, startDateTime: e.target.value }))}
            disabled={state.generating}
            required
          />

          {/* End Date */}
          <LabeledInputComponent
            label='End Date & Time'
            name='endDateTime'
            type='datetime-local'
            value={state.endDateTime}
            onChange={(e) => setState(s => ({ ...s, endDateTime: e.target.value }))}
            disabled={state.generating}
            required
          />

          {/* Info Message */}
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
            <p className='text-sm text-blue-800'>
              This report will include Agro Actions Summary for the selected date range.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-end gap-3 p-6 border-t border-gray-200'>
          <button
            onClick={handleClose}
            className='px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
            disabled={state.generating}
          >
            Cancel
          </button>
          <ButtonComponent
            onClick={handleGenerate}
            disabled={state.generating}
          >
            {state.generating ? (
              <>
                <svg className='animate-spin -ml-1 mr-2 h-4 w-4 text-white' fill='none' viewBox='0 0 24 24'>
                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                </svg>
                Generating...
              </>
            ) : (
              'Generate Report'
            )}
          </ButtonComponent>
        </div>
      </div>
    </ModalComponent>
  )
}

export default GenerateReportModal
